import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// src/plugins/runtime/PluginHost.tsx
// Wraps a plugin's rendered component with its PluginContext + an ErrorBoundary
// so a single plugin crash never takes down the app. The error surfaces as a
// kid-friendly message — not a stack trace.
import { Component, useEffect, useState } from 'react';
import { PluginContextProvider } from '../sdk/PluginContext';
import { load } from '../loader';
import { PluginPermissionError } from '../types';
export default function PluginHost({ pluginId, loading }) {
    const [loaded, setLoaded] = useState('pending');
    useEffect(() => {
        let alive = true;
        setLoaded('pending');
        load(pluginId).then(p => { if (alive)
            setLoaded(p); });
        return () => { alive = false; };
    }, [pluginId]);
    if (loaded === 'pending') {
        return (_jsx("div", { className: "mx-auto max-w-3xl px-6 py-10", children: loading ?? _jsx("p", { className: "text-sm text-slate-500", children: "Loading this plugin\u2026" }) }));
    }
    if (!loaded || !loaded.Component) {
        return (_jsx("div", { className: "mx-auto max-w-3xl px-6 py-10", children: _jsxs("div", { className: "rounded-xl bg-slate-50 p-6 ring-1 ring-slate-200", children: [_jsx("h2", { className: "text-base font-semibold text-slate-900", children: "This plugin isn't turned on" }), _jsx("p", { className: "mt-2 text-sm text-slate-600", children: "Either it isn't installed, you turned it off, or it has no page to show. Head to Plugins to turn it back on." })] }) }));
    }
    const { Component } = loaded;
    return (_jsx(PluginBoundary, { pluginId: pluginId, children: _jsx(PluginContextProvider, { pluginId: pluginId, children: _jsx(Component, {}) }) }));
}
// --- Local error boundary so plugin errors stay contained. -------------------
class PluginBoundary extends Component {
    state = { err: null };
    static getDerivedStateFromError(err) { return { err }; }
    componentDidCatch(err) {
        console.error(`[plugin:${this.props.pluginId}] crashed:`, err);
    }
    render() {
        if (!this.state.err)
            return this.props.children;
        const err = this.state.err;
        const isPerm = err instanceof PluginPermissionError;
        return (_jsx("div", { className: "mx-auto max-w-3xl px-6 py-10", children: _jsxs("div", { className: "rounded-xl bg-amber-50 p-6 ring-1 ring-amber-200", children: [_jsx("h2", { className: "text-base font-semibold text-amber-900", children: isPerm ? "This plugin tried to do something it isn't allowed to do" : "This plugin ran into a problem" }), _jsx("p", { className: "mt-2 text-sm text-amber-900/80", children: err.message }), _jsx("p", { className: "mt-2 text-xs text-amber-900/60", children: "The rest of the app is fine \u2014 you can keep working. Turn this plugin off in Plugins if it keeps happening." })] }) }));
    }
}
